/**
 * L'inclinaison d'une carte au pointeur (maquette `carte.html`, « Incline ton telephone, ou glisse le doigt : le
 * reflet suit »). Une carte 2D qui s'incline de quelques degres vers le doigt ou la souris, avec un reflet qui
 * suit ; la vraie 3D arrive avec le ticket 7. Coupee sous `prefers-reduced-motion`.
 *
 * Pur : ni DOM ni minuteur. Le composant lit les coordonnees du pointeur et la boite de la carte, `inclinaisonDe` en
 * tire la rotation et la place du reflet.
 */

/** L'angle maximal, en degres, quand le pointeur est au bord de la carte. */
export const INCLINAISON_MAX_DEG = 9;

export type Boite = { left: number; top: number; width: number; height: number };

export type Inclinaison = {
  /** Rotation autour de l'axe horizontal, en degres : positive, le haut de la carte recule. */
  rotationX: number;
  /** Rotation autour de l'axe vertical, en degres. */
  rotationY: number;
  /** La place du reflet, en pourcentage de la carte (0 a 100). */
  reflet: { x: number; y: number };
};

export const AU_REPOS: Inclinaison = { rotationX: 0, rotationY: 0, reflet: { x: 50, y: 35 } };

const borner = (valeur: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, valeur));

/**
 * L'inclinaison pour un pointeur en (`x`, `y`) sur la boite donnee, aux coordonnees de la page. Hors de la boite, le
 * pointeur est ramene au bord : la carte ne s'incline jamais au-dela de `max`. Une boite vide laisse la carte au repos.
 */
export function inclinaisonDe(
  point: { x: number; y: number },
  boite: Boite,
  max: number = INCLINAISON_MAX_DEG,
): Inclinaison {
  if (boite.width <= 0 || boite.height <= 0) {
    return AU_REPOS;
  }
  const relatifX = borner((point.x - boite.left) / boite.width, 0, 1);
  const relatifY = borner((point.y - boite.top) / boite.height, 0, 1);
  return {
    rotationX: Math.round((0.5 - relatifY) * 2 * max * 100) / 100,
    rotationY: Math.round((relatifX - 0.5) * 2 * max * 100) / 100,
    reflet: { x: Math.round(relatifX * 1000) / 10, y: Math.round(relatifY * 1000) / 10 },
  };
}

/** La transformation CSS d'une inclinaison. */
export function transformDe(inclinaison: Inclinaison): string {
  return `perspective(900px) rotateX(${inclinaison.rotationX}deg) rotateY(${inclinaison.rotationY}deg)`;
}

/** Vrai quand le visiteur a demande moins de mouvement. Sans `matchMedia` (un vieux navigateur), on s'abstient d'incliner. */
export function prefereMoinsDeMouvement(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
